/** Authorization, client side.
 *
 * Phase 2 moved authorization off identity — `is_superuser`, Django groups, a
 * boolean on the contact — and onto permissions held through roles. This is the
 * console's half of that: the server sends the effective permission set once,
 * and every affordance in the UI is gated on it.
 *
 * ## What a check here is, and is not
 *
 * These helpers decide what to *render*. They are not enforcement, and nothing
 * in this file protects anything: the server re-checks every request through
 * `core.authz`, which is the only place an answer actually counts. Hiding a
 * button the server would refuse is a courtesy to the user; showing one it would
 * refuse is a bug, not a vulnerability.
 *
 * That distinction is why the payload carries *effective* permissions — grants
 * already narrowed by module entitlement. If the console intersected grants with
 * licences itself, it would be a second implementation of a rule that lives in
 * `core.authz`, and the two would drift.
 *
 * ## Permissions, not roles
 *
 * Every helper takes a permission. Gating on a role name means renaming a role
 * silently changes who can press a button, and it makes a custom role — which
 * the model already supports — invisible to the UI. `roleNames` exists for
 * display only.
 *
 * Fails closed everywhere: no payload yet means no permission.
 */

/** Every permission the product defines.
 *
 * Mirrors `core.permissions_registry.PERMISSIONS`. A union rather than `string`
 * so a typo is a compile error — the failure mode otherwise is a button that
 * silently never renders, which is invisible in review and in tests.
 *
 * `authz.registry.test.ts` reads the Python registry and fails if the two lists
 * drift apart — a permission deleted there but left here would still compile,
 * and every check against it would silently be false forever.
 */
export type Permission =
  | 'console.access'
  | 'ticket.view'
  | 'ticket.view_all'
  | 'ticket.create'
  | 'ticket.update'
  | 'ticket.assign'
  | 'ticket.close'
  | 'ticket.delete'
  | 'ticket.comment'
  | 'ticket.comment_internal'
  | 'change.view'
  | 'change.create'
  | 'change.update'
  | 'change.transition'
  | 'change.approve'
  | 'change.override_freeze'
  | 'problem.view'
  | 'problem.manage'
  | 'asset.view'
  | 'asset.create'
  | 'asset.edit'
  | 'asset.delete'
  | 'knowledge.view'
  | 'knowledge.view_internal'
  | 'knowledge.create'
  | 'knowledge.edit'
  | 'knowledge.publish'
  | 'knowledge.delete'
  | 'catalog.view'
  | 'catalog.manage'
  | 'workflow.view'
  | 'workflow.edit'
  | 'workflow.execute'
  | 'report.view'
  | 'report.export'
  | 'audit.view'
  | 'security.view'
  | 'security.manage'
  | 'compliance.view'
  | 'compliance.manage'
  | 'compliance.assess'
  | 'risk.view'
  | 'risk.manage'
  | 'approval.view_own'
  | 'approval.decide'
  | 'approval.view_all'
  | 'approval.manage'
  | 'organization.read'
  | 'organization.update'
  | 'user.view'
  | 'user.invite'
  | 'user.update'
  | 'user.disable'
  | 'team.view'
  | 'team.manage'
  | 'role.view'
  | 'role.manage'
  | 'settings.view'
  | 'settings.manage'
  | 'email.view'
  | 'email.manage'
  | 'api.manage'
  | 'license.view'
  | 'license.manage'
  | 'platform.manage'
  | 'platform.support'
  | 'platform.billing'

/** The optional modules a permission can be gated behind. */
export type ModuleKey =
  | 'assets'
  | 'automation'
  | 'catalog'
  | 'change'
  | 'compliance'
  | 'knowledge'

export interface AgentRole {
  key: string
  name: string
  scope: 'organization' | 'platform'
  /** Null for a platform role — it is held everywhere, not in one tenant. */
  organization_id: number | null
  /** Null is permanent. Temporary access is a first-class thing here. */
  expires_at: string | null
}

export interface AgentOrganizationScope {
  id: number
  name: string
  code: string
}

/** `GET /api/agent/me/`. */
export interface AgentMe {
  name: string
  email: string
  agent_id: number
  /** `{id, name}` — the queue's share control needs an id, and a team name is
   *  a label rather than an identifier. */
  teams: { id: number; name: string }[]
  /** The organisation the permissions below were resolved for. */
  organization: AgentOrganizationScope | null
  roles: AgentRole[]
  /** Effective — grants already narrowed by module entitlement. */
  permissions: Permission[]
  /** What the caller may do across every tenant, not just the one in scope. */
  platform: { permissions: Permission[] }
  modules: Record<ModuleKey, boolean>
}

/** Fails closed: no payload, no permission. */
export function can(me: AgentMe | undefined, permission: Permission): boolean {
  return me?.permissions.includes(permission) ?? false
}

export function canAny(me: AgentMe | undefined, permissions: Permission[]): boolean {
  return permissions.some((permission) => can(me, permission))
}

export function canAll(me: AgentMe | undefined, permissions: Permission[]): boolean {
  return permissions.length > 0 && permissions.every((permission) => can(me, permission))
}

/** Whether an optional module is switched on for the organisation in scope.
 *
 *  Distinct from holding a permission: a module that is off should hide a whole
 *  screen, where a missing permission hides one action on it.
 */
export function moduleEnabled(me: AgentMe | undefined, module: ModuleKey): boolean {
  return me?.modules[module] ?? false
}

/** A permission held across every tenant rather than in the one in scope. */
export function canOnPlatform(me: AgentMe | undefined, permission: Permission): boolean {
  return me?.platform.permissions.includes(permission) ?? false
}

/** Whether the caller holds any platform role at all. */
export function isPlatformUser(me: AgentMe | undefined): boolean {
  return (me?.roles ?? []).some((role) => role.scope === 'platform')
}

/** Role names, for display. Never for a decision — see the note at the top. */
export function roleNames(me: AgentMe | undefined): string[] {
  return (me?.roles ?? []).map((role) => role.name)
}

/** Assignments that lapse, soonest first. Empty when access is all permanent. */
export function expiringRoles(me: AgentMe | undefined): AgentRole[] {
  return (me?.roles ?? [])
    .filter((role) => role.expires_at !== null)
    .sort((a, b) => (a.expires_at ?? '').localeCompare(b.expires_at ?? ''))
}
